import * as THREE from "three";
import gsap from "gsap";

/**
 * @typedef {{ x: number, y: number, z: number }} CameraVector
 * @typedef {{ position: CameraVector, target: CameraVector }} CameraPose
 */

export function createCameraController() {
  const initialPosition = new THREE.Vector3();
  const initialTarget = new THREE.Vector3();
  const positionBuffer = new THREE.Vector3();
  const targetBuffer = new THREE.Vector3();
  let camera = null;
  let target = null;
  let hasInitialState = false;

  function lookAtTarget() {
    if (camera && target) camera.lookAt(target.position);
  }

  return {
    attach(nextCamera, nextTarget, { applyIntroOffset = false } = {}) {
      camera = nextCamera;
      target = nextTarget;
      hasInitialState = Boolean(camera && target);

      if (!hasInitialState) return false;

      lookAtTarget();
      initialPosition.copy(camera.position);
      initialTarget.copy(target.position);

      if (applyIntroOffset) {
        camera.position
          .sub(target.position)
          .multiplyScalar(1.12)
          .add(target.position);
        lookAtTarget();
      }

      return true;
    },
    isReady: () => hasInitialState,
    getCamera: () => camera,
    getAnimationTargets: () =>
      camera && target ? [camera.position, target.position] : [],
    resize({ width, height }) {
      if (!camera) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    },
    animateToInitial(onComplete) {
      if (!hasInitialState) return false;

      gsap.to(camera.position, {
        duration: 2.2,
        x: initialPosition.x,
        y: initialPosition.y,
        z: initialPosition.z,
        ease: "power3.inOut",
        onUpdate: lookAtTarget,
        onComplete,
      });
      return true;
    },
    getPose(positionObject, targetObject) {
      if (!positionObject || !targetObject) return null;

      positionObject.updateWorldMatrix(true, false);
      positionObject.getWorldPosition(positionBuffer);
      targetObject.updateWorldMatrix(true, false);
      targetObject.getWorldPosition(targetBuffer);

      return {
        position: {
          x: positionBuffer.x,
          y: positionBuffer.y,
          z: positionBuffer.z,
        },
        target: {
          x: targetBuffer.x,
          y: targetBuffer.y,
          z: targetBuffer.z,
        },
      };
    },
    createTimeline() {
      return gsap.timeline({ onUpdate: lookAtTarget });
    },
    appendFocus(timeline, pose) {
      timeline
        .to(
          camera.position,
          {
            duration: 3,
            ...pose.position,
            ease: "power1.inOut",
          },
          0,
        )
        .to(
          target.position,
          {
            duration: 3.5,
            ...pose.target,
            ease: "power3.inOut",
          },
          0,
        );
    },
    appendReset(timeline) {
      timeline
        .to(
          camera.position,
          {
            duration: 3,
            x: initialPosition.x,
            y: initialPosition.y,
            z: initialPosition.z,
            ease: "power2.inOut",
          },
          0,
        )
        .to(
          target.position,
          {
            duration: 3,
            x: initialTarget.x,
            y: initialTarget.y,
            z: initialTarget.z,
            ease: "power2.inOut",
          },
          0,
        );
    },
  };
}
